import { reaction } from 'mobx';
import PropTypes from 'prop-types';
import { apiCall } from '../../API';
import { createParagraphNode, htmlToSlate, isNotEmpty } from '../../Components/Fuse/FuseWysiwyg/utils';
import debug from '../../debug';
import BoolStore from '../helpers/BoolStore';
import RawValueStore from '../helpers/RawValueStore';
import { MetadataPropType, CUSTOMIZATION_TYPE } from './CustomizeUIConstants';
import { BrandingOption, HelpButton, HelpButtonAction } from '../BrowserBarOptions';
import EntityFormStore from '../helpers/EntityFormStore';
import TextStore from '../helpers/TextStore';
import ValueStore from '../helpers/ValueStore';

export default class CustomizeBrowserBarStore extends EntityFormStore {
    static _PropType = {
        ...EntityFormStore._PropType,
        brandingOption: ValueStore.PropTypes,
        customBrandingText: TextStore.PropTypes,
        helpButton: ValueStore.PropTypes,
        helpButtonAction: ValueStore.PropTypes,
        externalLink: TextStore.PropTypes,
        popupContent: RawValueStore.PropTypes,
        customBrandingTextMaxLength: PropTypes.number,
        isCustomBrandingEditable: PropTypes.func,
        isExternalLinkEditable: PropTypes.func,
        isPopupContentEditable: PropTypes.func,
        metadata: MetadataPropType,
        visible: BoolStore.PropTypes,
    };

    static PropType = PropTypes.shape(CustomizeBrowserBarStore._PropType);

    constructor(data = { }) {
        super(/** @class CustomizeBrowserBarStore */{
            brandingOption: new ValueStore(BrandingOption.DEFAULT),
            customBrandingText: new TextStore(),
            helpButton: new ValueStore(HelpButton.DISABLED),
            helpButtonAction: new ValueStore(HelpButtonAction.LINK),
            externalLink: new TextStore(),
            popupContent: new RawValueStore(createParagraphNode())
        }, /** @class CustomizeBrowserBarStore */{
            apiLoad: apiCall('/console/browser_bar/get'),
            apiUpdate: apiCall('/console/browser_bar/update'),
            customBrandingTextMaxLength: 32,
            metadata: {
                customizationType: CUSTOMIZATION_TYPE.BROWSER_BAR,
                type: 'Browser Bar',
                name: 'Branding Text and Help Link',
                description: 'Customize Browser Bar text and help link.',
            },
            visible: new BoolStore(true),
            isCustomBrandingEditable: () => this.brandingOption.equals(BrandingOption.CUSTOM),
            isExternalLinkEditable: () => !this.helpButton.equals(HelpButton.DISABLED)
                && this.helpButtonAction.equals(HelpButtonAction.LINK),
            isPopupContentEditable: () => !this.helpButton.equals(HelpButton.DISABLED)
                && this.helpButtonAction.equals(HelpButtonAction.POPUP),
        }, data);

        this.customBrandingText.setValidator((value) => {
            if (!value) {
                return false;
            }
            if (value.length > this.customBrandingTextMaxLength) {
                return `Custom branding cannot exceed ${this.customBrandingTextMaxLength} characters.`;
            }
            return true;
        });

        this.externalLink.setValidator((value) => {
            if (!value) {
                return false;
            }
            try {
                const url = new window.URL(value);
                if (url.protocol === 'http:' || url.protocol === 'https:') {
                    return true;
                }
            } catch (e) { /* whatever */ }

            return 'URL is invalid';
        });

        this.popupContent.setValidator(isNotEmpty);

        // focus custom branding text after custom branding option selected
        reaction(() => this.editModal.isOpen
                && this.brandingOption.equals(BrandingOption.CUSTOM),
        (cond) => cond && setTimeout(this.customBrandingText.focus));

        // select external link input when it shows up
        reaction(() => this.editModal.isOpen
                && !this.helpButton.equals(HelpButton.DISABLED)
                && this.helpButtonAction.equals(HelpButtonAction.LINK),
        (cond) => cond && setTimeout(this.externalLink.focus));

        // select "no link" help link option if custom branding was a link option and user switched to "default branding"
        reaction(() => this.editModal.isOpen
                && this.brandingOption.equals(BrandingOption.DEFAULT)
                && this.helpButton.equals(HelpButton.BRANDING),
        (cond) => cond && this.helpButton.setValue(HelpButton.DISABLED));

        // focus custom branding text after custom branding option selected
        reaction(() => this.editModal.isOpen
                && !this.helpButton.equals(HelpButton.DISABLED)
                && this.helpButtonAction.equals(HelpButtonAction.POPUP),
        (cond) => cond && setTimeout(this.popupContent.focus));
    }

    _load = this.load;
    load = (...args) => this._load(...args).then((result) => {
        if (!result?.popupContent && result?.popupMessage) {
            // combine headline and deserialized message into slate format in content value
            this.popupContent.setValue(htmlToSlate(result.popupMessage));
        }

        return result;
    });

    isValid = () => {
        if (this.isCustomBrandingEditable() && !this.customBrandingText.isValid()) {
            return false;
        }
        if (this.isExternalLinkEditable() && !this.externalLink.isValid()) {
            return false;
        }
        if (this.isPopupContentEditable() && !this.popupContent.isValid()) {
            return false;
        }
        return true;
    };
}
